import { PortfolioType } from '@ddt-web/config';
import { OldPortfolio, PortfolioConstants, WebUserDataConstants } from '@ddt-web/domain';
import { ModelNameUtil } from '@ddt-web/generic-utils';
import * as cloneDeep from 'lodash/cloneDeep';
import * as isNil from 'lodash/isNil';

export class PortfolioUtil {

	/**
	 * Returns the portfolio name of the given portfolio object.
	 * If the provided portfolio does not have a name, 'No Name' value is returned.
	 *
	 * @param portfolio
	 */
	public static getPortfolioName(portfolio: any): string {
		return portfolio.description || (portfolio['getName'] && portfolio.getName()) || 'No Name';
	}

	/**
	 * Returns true if the portfolio name is defined.
	 *
	 * @param portfolio
	 * @returns true if portfolio name is defined, false otherwise
	 */
	public static isValidPortfolioName(portfolio: any): boolean {
		const portfolioName = portfolio.description || (portfolio['getName'] && portfolio.getName());

		return !isNil(portfolioName);
	}

	/**
	 * Returns the user-friendly subType string of the given portfolio.
	 * If the provided portfolio does not have valid subType, a missing data message is returned.
	 *
	 * @param portfolio
	 * @returns portfolio subType, otherwise undefined
	 */
	public static getPortfolioSubTypeString(portfolio: any): string {
		let subTypeString,
			partnerSourceString = '';
		const subType = (portfolio && portfolio instanceof OldPortfolio && portfolio.getSubType()) || portfolio.subType;
		const partnerSource = portfolio.partnerSource;

		if (subType === WebUserDataConstants.SUBTYPE.EXTERNAL_PORTFOLIO) {
			return PortfolioConstants.EXTERNAL_SOURCE?.[portfolio.originPlatform?.platform?.toUpperCase()] || PortfolioConstants.MISSING_DATA_VALUE;

		} else if (subType) {
			subTypeString = PortfolioConstants.PORTFOLIO_CATEGORY[subType];

			if (partnerSource) {
				partnerSourceString = PortfolioConstants.EXTERNAL_SOURCE[partnerSource];
			}
		}

		return typeof subTypeString !== 'undefined' ? (partnerSourceString + ' ' + subTypeString).trim() : PortfolioConstants.MISSING_DATA_VALUE;
	}

	/**
	 * Given a subType and an optional partner, returns the portfolio type. If no portfolio type is found, return a default portfolio type
	 * @param subType portfolio subType
	 * @param partner if on partner mode, it will pass the partner name.
	 */
	public static getPortfolioTypeFromSubType(subType: string, partner?: string): string {
		const portfolioType = PortfolioConstants.PORTFOLIO_CATEGORY[subType];
		// if subType is third party
		if (subType === WebUserDataConstants.SUBTYPE.PARTNER_MODEL) {
			return partner && PortfolioConstants.EXTERNAL_SOURCE[partner.toUpperCase()]
				? PortfolioConstants.EXTERNAL_SOURCE[partner.toUpperCase()] + ' ' + portfolioType
				: PortfolioConstants.EXCEL_DEFAULT_PORTFOLIO_TYPE;
		} else {
			return portfolioType ? portfolioType : PortfolioConstants.EXCEL_DEFAULT_PORTFOLIO_TYPE;
		}
	}

	/**
	 * Returns the user-friendly subType string of the given string.
	 * If the provided string is not a valid subType, a missing data message is returned.
	 *
	 * @param subType
	 * @returns portfolioSubType, otherwise undefined
	 */
	public static getSubTypeString(subType: string): string {
		const portfolioSubType: string = PortfolioConstants.PORTFOLIO_CATEGORY[subType];

		return typeof portfolioSubType !== 'undefined' ? portfolioSubType : PortfolioConstants.MISSING_DATA_VALUE;
	}

	/**
	 * Returns the portfolio last modified date of the given portfolio object.
	 * If the provided portfolio does not have a last modified date, null is returned.
	 *
	 * @param portfolio
	 * @returns lastModifiedDate
	 */
	public static getPortfolioLastModifiedDate(portfolio: any): number {
		const lastModifiedDate = (portfolio && portfolio instanceof OldPortfolio && portfolio.getLastModifiedDate()) || portfolio.dateLastModified || null;

		return lastModifiedDate;
	}

	public static getPortfolioNameFromStore(associatedPortfolios: any, modelsConfig?: any): string {
		const portfolioType = associatedPortfolios?.type;

		switch (portfolioType) {
			case PortfolioType.PORTFOLIO:
				return associatedPortfolios?.description ?? '';
			case PortfolioType.MODEL:
				return ModelNameUtil.getModelName(modelsConfig, associatedPortfolios);
			case PortfolioType.THIRD_PARTY:
				return ModelNameUtil.getModelName(modelsConfig, associatedPortfolios);
			default:
				return associatedPortfolios?.description;
		}
	}

	/**
	 * Checks if the provided portfolio (eg associatedPortfolios entity) has any errors:
	 * - errorCode or error at the level of portfolio
	 * - any missing securities if checkForMissingSecurities is true 
	 * 
	 * @param portfolio entity object from associatedPortfolios 
	 * @param checkForMissingSecurities if true, it checks if the provided portfolio has missing securities
	 * @returns true if portfolios has error, false otherwise
	 */
	public static isPortfolioError(portfolio: any, checkForMissingSecurities: boolean = false): boolean {
		const isErrorPortfolios = portfolio?.['errorCode'] || portfolio?.['error'];

		let hasMissingSecurities = false;
		if (checkForMissingSecurities) {
			const children = PortfolioUtil.getAllHoldingsChildren(portfolio) ?? [];
			hasMissingSecurities = children.some(child => child.error);
		}

		return isErrorPortfolios || hasMissingSecurities;
	}

	/**
	 * Enriches securities of the provided portfolios with the response received from securityDetails.
	 * 
	 * @param portfolios partner portfolios + portfolios included in blend
	 * @param securityDetails securityDetails response
	 */
	public static updateSecuritiesWithSecurityData(portfolios: Array<any>, securityDetails: any, portfolioChildrenKey: string): any {
		const securities = securityDetails?.resultMap?.SECURITY?.filter(security => security.securityId) ?? [];

		return portfolios
			.filter(portfolio => portfolio?.[portfolioChildrenKey])
			.map(portfolio => {
				const updatedChildren = portfolio[portfolioChildrenKey].map(child => {
					const updatedSecurity = securities.find(security =>
						security.securityId === child.commonIdentifier || // the typical case
						// the fallback case when an apb engagement securityId has changed 
						// will overwrite the securityId in associatedPortfolios with the most up-to-date securityId from security-data
						security.requestId === child.commonIdentifier
					) || {};
					return { ...child, ...updatedSecurity };
				});

				const portfolioClone = cloneDeep(portfolio);
				portfolioClone[portfolioChildrenKey] = updatedChildren;

				return portfolioClone;
			});
	}

	/**
	 * Returns the children as they are from the provided portfolio (accounts for blending as well).
	 * Note: this does not flatten the portfolio -> the children will not be unique/ with overall weight.
	 * This can be used to identify missing securities, when error object is provided.
	 * 
	 * @param portfolio 
	 * @returns all children from all levels of the provided portfolio
	 */
	private static getAllHoldingsChildren(portfolio: any): any {
		return portfolio?.children?.map(child => child.portfolio ? PortfolioUtil.getAllHoldingsChildren(child.portfolio) : child).reduce((acc, value) => acc.concat(value), []);
	}
}
